import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ShopLayout from '../components/shop/ShopLayout';
import ProductCard from '../components/shop/ProductCard';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import {
  resolveImageUrl,
  formatPrice,
  getEffectivePrice,
  isInStock,
  mapApiProductToCard,
  addRecentlyViewed,
  getRecentlyViewed,
  getProductUrl
} from '../utils/shopHelpers';
import { useWishlist } from '../context/WishlistContext';
import { useNavigate } from 'react-router-dom';
import '../pages/Shop.css';
import './ProductDetailPage.css';
import Seo, { buildBreadcrumbSchema, buildProductSchema } from '../components/seo/Seo';
import { absoluteUrl, productSeo } from '../config/seo';

function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.getPublicProductById(productId);
        const data = res?.data;
        if (!cancelled) {
          setProduct(data);
          setRelated(Array.isArray(data?.related_products) ? data.related_products : []);
          if (data) addRecentlyViewed(data);
        }
      } catch (e) {
        if (!cancelled) {
          setProduct(null);
          setError(e.message || 'Product not found');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const images = product?.images?.length
    ? product.images.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    : product?.primary_image_url
      ? [{ image_url: product.primary_image_url, is_primary: true }]
      : [];

  const galleryUrls = images.map((img) =>
    resolveImageUrl(img.image_url, product?.sku || 'Product')
  );

  const inStock = product ? isInStock(product) : false;
  const price = product ? formatPrice(getEffectivePrice(product)) : '';

  const handleAddToCart = () => {
    if (!product || !inStock) return;
    addItem(mapApiProductToCard(product), qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const recentlyViewed = getRecentlyViewed()
    .filter((p) => p.id !== product?.id)
    .slice(0, 4)
    .map(mapApiProductToCard);

  const handleWishlist = async () => {
    if (!product) return;
    try {
      await toggleWishlist(product.id);
    } catch (err) {
      if (err.message === 'LOGIN_REQUIRED') {
        navigate('/login', { state: { from: { pathname: getProductUrl(product) } } });
      }
    }
  };
  const breadcrumbs = product
    ? [
        ...(product.category_slug
          ? [{ label: product.category_name, to: `/shop/category/${product.category_slug}` }]
          : []),
        { label: product.name }
      ]
    : [{ label: 'Product' }];

  const seo = product
    ? productSeo(product)
    : { title: 'Product | Chesi', description: 'AIDC product details', path: `/shop/product/${productId}` };
  const productImage = product
    ? resolveImageUrl(
        product.primary_image_url || product.images?.[0]?.image_url,
        product.sku || product.name
      )
    : undefined;

  return (
    <ShopLayout breadcrumbs={breadcrumbs}>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        image={productImage}
        type="product"
        jsonLd={
          product
            ? [
                buildProductSchema(
                  { ...product, primary_image_url: productImage },
                  absoluteUrl(seo.path)
                ),
                buildBreadcrumbSchema([
                  { name: 'Shop', url: '/shop' },
                  ...(product.category_slug
                    ? [{ name: product.category_name, url: `/shop/category/${product.category_slug}` }]
                    : []),
                  { name: product.name, url: seo.path }
                ])
              ]
            : null
        }
      />
      <div className="container product-detail">
        {loading && <p className="product-detail__status">Loading product…</p>}
        {error && !loading && (
          <p className="product-detail__status product-detail__status--error">{error}</p>
        )}
        {product && !loading && (
          <>
            <div className="product-detail__grid">
              <div className="product-detail__gallery">
                <div className="product-detail__main-image">
                  <img
                    src={galleryUrls[activeImage] || galleryUrls[0]}
                    alt={product.name}
                  />
                </div>
                {galleryUrls.length > 1 && (
                  <div className="product-detail__thumbs">
                    {galleryUrls.map((url, i) => (
                      <button
                        key={i}
                        type="button"
                        className={i === activeImage ? 'active' : ''}
                        onClick={() => setActiveImage(i)}
                      >
                        <img src={url} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="product-detail__info">
                {product.brand_name && (
                  <p className="product-detail__brand">{product.brand_name}</p>
                )}
                <h1 className="product-detail__title">{product.name}</h1>
                <p className="product-detail__sku">SKU: {product.sku}</p>
                <p className="product-detail__price">{price}</p>
                <p className={`product-detail__stock ${inStock ? 'in-stock' : 'out-of-stock'}`}>
                  {inStock ? `In Stock (${product.stock_quantity} available)` : 'Out of Stock'}
                </p>

                {product.short_description && (
                  <p className="product-detail__short">{product.short_description}</p>
                )}

                <div className="product-detail__purchase">
                  <div className="product-detail__qty">
                    <label htmlFor="detail-qty">Quantity</label>
                    <input
                      id="detail-qty"
                      type="number"
                      min="1"
                      max={inStock ? Math.max(product.stock_quantity, 99) : 1}
                      value={qty}
                      disabled={!inStock}
                      onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                    />
                  </div>
                  <button
                    type="button"
                    className="product-detail__add-cart"
                    disabled={!inStock}
                    onClick={handleAddToCart}
                  >
                    {added ? 'Added to Cart ✓' : 'Add to Cart'}
                  </button>
                </div>

                <div className="product-detail__meta">
                  <button type="button" className="product-detail__wishlist" onClick={handleWishlist}>
                    {product && isWishlisted(product.id) ? '♥ In Wishlist' : '♡ Add to Wishlist'}
                  </button>
                  <button type="button" className="product-detail__share" onClick={() => navigator.share?.({ title: product.name, url: window.location.href })}>
                    Share
                  </button>
                </div>
              </div>
            </div>

            <div className="product-detail__tabs">
              <div className="product-detail__tab-nav">
                {['description', 'specifications'].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={activeTab === tab ? 'active' : ''}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              <div className="product-detail__tab-panel">
                {activeTab === 'description' && (
                  <div
                    className="product-detail__description"
                    dangerouslySetInnerHTML={{
                      __html: product.long_description || product.short_description || '<p>No description available.</p>'
                    }}
                  />
                )}
                {activeTab === 'specifications' && (
                  <table className="product-detail__specs">
                    <tbody>
                      <tr><th>SKU</th><td>{product.sku}</td></tr>
                      {product.brand_name && <tr><th>Brand</th><td>{product.brand_name}</td></tr>}
                      {product.category_name && <tr><th>Category</th><td>{product.category_name}</td></tr>}
                      <tr><th>Availability</th><td>{inStock ? 'In Stock' : 'Out of Stock'}</td></tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {related.length > 0 && (
              <section className="product-detail__related">
                <h2>Related Products</h2>
                <div className="product-detail__related-grid">
                  {related.map((p) => (
                    <ProductCard key={p.id} product={mapApiProductToCard(p)} />
                  ))}
                </div>
              </section>
            )}

            {recentlyViewed.length > 0 && (
              <section className="product-detail__related">
                <h2>Recently Viewed</h2>
                <div className="product-detail__related-grid">
                  {recentlyViewed.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </ShopLayout>
  );
}

export default ProductDetailPage;
